import React, { useContext } from 'react';

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import { AuctionEndCountdown } from
  'src/pages/CatalogPage/components/CatalogHead/AuctionEndCountdown/AuctionEndCountdown';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);


import { useOverlayTriggerState } from '@react-stately/overlays';
import { Banner, Button, Grid, GridItem, Accordion, AccordionItem, Typography, Breadcrumb, Modal, Grid2 } from '@riac/design-system';
import { AuctionSummary } from 'src/pages/CatalogPage/components/AuctionSummary/AuctionSummary';

import { ICatalogSearch } from 'src/pages/CatalogPage/CatalogPage.i';
import { EBookDownload } from 'src/components/EBookDownload/EBookDownload';
import { IconClock } from '@riac/design-system';
import { AuctionLocation } from 'src/pages/AuctionsPage/AuctionsPage.style';
import { GenericPageHeader } from 'src/components/GenericPageHeader';

interface ICatalogHead {
  excerpt: string;
  startTime: string;
  startDate: string;
  auctionTypeID: number;
  keywords: string;
  lotNumber: string;
  updateSearch: ICatalogSearch;
  complete: boolean;
  searchPostData: any;
  sessions: Array<object>;
  locationAddress: string;
  locationCity: string;
  locationState: string;
  locationZip: string;
  onlyLotsBidOn: boolean;
}

export const CatalogHead = (props: ICatalogHead): JSX.Element => {
  const { auctionStartDate, auctionEndDate, auctionEndTime,
    auctionCloseRate, lotExtensionTime, auctionTitle, auctionSubtitle, auctionTypeId, auctionId,
    auctionLocCity, auctionLocState,
  } = useContext(
    CatalogContext
  );

  const auctionSummaryModalState = useOverlayTriggerState({});

  let whenIsIt:string;

  // DayJS Note: Dates compare with timezone.
  const auctionEnded = dayjs() > auctionEndDate.add(1, 'day');

  // DayJS Note: Interpreting in day/time separately is OK without converting to America/Chicago.
  const endTime = auctionEndTime ? dayjs(auctionEndTime, 'HH:mm:ss').format('h:mm A') : '6:00 PM';
  const endHours = auctionEndTime ? parseInt(dayjs(auctionEndTime, 'HH:mm:ss').format('H')) : 18;

  if(auctionTypeId === TIMED_AUCTION_TYPE_ID) {
    whenIsIt
      = `Ending at ${auctionEndDate.format('MMMM DD, YYYY')}, ${endTime} `
      + `${auctionEndDate.format('z')}*`;
  } else {
    whenIsIt = auctionStartDate.format('MMMM DD, YYYY');
    if(!auctionStartDate.isSame(auctionEndDate, 'day')) {
      whenIsIt += ` to ${auctionEndDate.format('MMMM DD, YYYY')}`;
    }
    if(!auctionEnded) {
      whenIsIt += ` | Starting at ${dayjs(props.startTime, 'HH:mm:ss').format('h:mm A')}`;
      if(!auctionStartDate.isSame(auctionEndDate, 'day')) {
        whenIsIt += ' Each Day';
      }
    }
  }

  const titleAddonContent = <>
    {((props.auctionTypeID == 1) || (props.auctionTypeID == 2) || (auctionId === 2094)) &&
      <div className='flex flex-col items-center h-full'>
        <Button size='lg' variant='secondary' className='border-white' onPress={() => {
          auctionSummaryModalState.open();
        }}>
          View Times
        </Button>
      </div>
    }
    <EBookDownload
      auctionId={auctionId}
      searchPostData={props.searchPostData}
      title = {`Catalog #${auctionId}`}
      label = 'Catalog'
      pdfTitle = {`Catalog #${auctionId}`}
      noOutline
    />
  </>

  return (
    <div>
      <GenericPageHeader
        gridClassName='pb-4'
        breadcrumbs={[
          { slug: '/gun-auctions', text: 'Auctions' },
          { slug: '#', text: auctionTitle },
        ]}
        title={<Typography expressive display className='text-[32px] leading-9 font-semibold'>{auctionTitle}</Typography>}
        description={<div className='flex flex-col gap-5'>
          <Typography color='primary' weight='bold'>{auctionSubtitle}</Typography>
          {/*<Typography color='text-light' weight='semibold'>{`${auctionLocCity}, ${auctionLocState}`}</Typography>*/}
          {(auctionTypeId === TIMED_AUCTION_TYPE_ID && auctionEndDate.add(endHours, 'hour').isAfter(dayjs())) && (
            <div className='bg-neutral-600 p-2 flex flex-col gap-1 justify-start my-4'>
              <Typography className='text-text-contrast flex flex-row gap-1 items-center justify-start' weight='bold'>
                <IconClock/> <AuctionEndCountdown endTime={auctionEndDate.add(endHours, 'hour')} />
              </Typography>
            </div>
          )}
          <div className='flex flex-col gap-0'>
            <Typography size='lg' className='text-text md:text-text-contrast'>{whenIsIt}</Typography>
            {auctionTypeId === TIMED_AUCTION_TYPE_ID && (
              <Typography className='text-text-light md:text-neutral-300 light' size='xs'>
                * Lots end individually, {auctionCloseRate} second{auctionCloseRate > 1 ? 's' : ''} apart.{' '}
                New bids will extend each lot by {lotExtensionTime} minute{lotExtensionTime > 1 ? 's' : ''}.
              </Typography>
            )}
          </div>
          <div className='flex flex-col gap-5 items-start'>
            {dayjs() > auctionEndDate.add(1, 'day') ? (
              <Banner
                color='warning'
                size='sm'
              >
                This auction has ended.
              </Banner>
            ) : null}
          </div>
          <div className='flex-row gap-3 items-center flex-wrap flex md:hidden h-max'>
            {titleAddonContent}
          </div>
        </div>}
        titleAddonContainerClassName='hidden md:flex'
        titleAddon={
          <div className='flex-row gap-3 items-center flex-wrap hidden md:flex h-max'>
            {titleAddonContent}
          </div>
        }
      />
      <Modal isDismissable showCloseButton type='narrow' onClose={auctionSummaryModalState.close} isOpen={auctionSummaryModalState.isOpen} title='Auction Time Summary' footerButtons={<>
        <Button onPress={auctionSummaryModalState.close}>Close</Button>
      </>}>
        <AuctionSummary
          auctionStartTime = {props.startTime}
          auctionStartDate = {props.startDate}
          auctionTypeID = {props.auctionTypeID}
          auctionExcerpt = {props.excerpt}
          sessions = {props.sessions}
          locationAddress={props.locationAddress}
          locationCity={props.locationCity}
          locationState={props.locationState}
          locationZip={props.locationZip}
        />
      </Modal>
    </div>
  );
};
